import React, { useEffect, useState } from "react";
import { Heart, Send } from "lucide-react";
import { toast } from "sonner";
import useReveal from "../hooks/useReveal";
import { getGuestbook, addGuestbookEntry } from "../lib/api";

export default function Guestbook() {
  const [ref, visible] = useReveal();
  const [entries, setEntries] = useState([]);
  const [name, setName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const data = await getGuestbook();
      setEntries(data);
    } catch (e) {
      setEntries([]);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !message.trim()) {
      toast.error("Please add your name and a message.");
      return;
    }
    setLoading(true);
    try {
      await addGuestbookEntry({ name: name.trim(), message: message.trim() });
      toast.success("Thank you for your kind words!");
      setName("");
      setMessage("");
      await load();
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 md:py-32 px-6">
      <div ref={ref} className={`max-w-[1000px] mx-auto reveal ${visible ? "is-visible" : ""}`}>
        <div className="text-center mb-12">
          <h2 className="font-serif-display text-[#9E2B25] text-[46px] md:text-[64px]">Well Wishes</h2>
          <p className="mt-4 text-[#4a443d] max-w-xl mx-auto">
            Leave a note for the couple. Your message will appear below for everyone to enjoy.
          </p>
        </div>

        <form onSubmit={submit} className="max-w-xl mx-auto">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
            className="w-full bg-transparent border-b border-[#211E1A]/25 py-3 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors"
          />
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Share your wishes for the happy couple..."
            rows={3}
            className="mt-6 w-full bg-transparent border-b border-[#211E1A]/25 py-3 text-[#211E1A] placeholder:text-[#9c9488] focus:outline-none focus:border-[#9E2B25] transition-colors resize-none"
          />
          <button
            type="submit"
            disabled={loading}
            className="mt-6 inline-flex items-center gap-2 bg-[#9E2B25] text-[#F4EFE6] text-[12px] uppercase tracking-widest-2 px-8 py-[14px] rounded-sm hover:bg-[#872019] transition-colors duration-300 disabled:opacity-60"
          >
            <Send size={15} /> {loading ? "Sending..." : "Sign the Guestbook"}
          </button>
        </form>

        {entries.length > 0 && (
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {entries.map((en) => (
              <div key={en.id} className="bg-[#F9F5EE] border border-[#211E1A]/8 p-6 rounded-sm shadow-sm">
                <Heart size={16} className="text-[#9E2B25]" />
                <p className="mt-3 text-[#4a443d] italic leading-relaxed">“{en.message}”</p>
                <p className="mt-4 text-[12px] uppercase tracking-widest-2 text-[#211E1A] font-semibold">
                  {en.name}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
